import { Binary } from "@openanalyst-ai/core/util/binary"
import type { OpenAnalystClient, Session } from "@openanalyst-ai/sdk/v2/client"
import { produce, type SetStoreFunction } from "solid-js/store"
import type { State } from "@/context/global-sync/types"
import { compactSessionTime } from "./helpers"

export const ARCHIVED_CHATS_LIMIT = 200
type ArchivedListClient = {
  experimental: {
    session: {
      list(input: {
        directory: string
        roots: true
        archived: true
        limit: number
      }): Promise<{ data?: Session[] }>
    }
  }
}

export async function loadArchivedChats(input: {
  client: ArchivedListClient
  directory: string
  limit?: number
}) {
  const result = await input.client.experimental.session.list({
    directory: input.directory,
    roots: true,
    archived: true,
    limit: input.limit ?? ARCHIVED_CHATS_LIMIT,
  })
  return result.data ?? []
}

export function sortedArchivedChats(sessions: Session[]) {
  return sessions
    .filter((session) => !session.parentID && session.time.archived !== undefined)
    .sort((a, b) => {
      const archivedDelta = (b.time.archived ?? 0) - (a.time.archived ?? 0)
      if (archivedDelta !== 0) return archivedDelta
      const updatedDelta = (b.time.updated ?? b.time.created) - (a.time.updated ?? a.time.created)
      if (updatedDelta !== 0) return updatedDelta
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
    })
}

export function removeArchivedChat(sessions: Session[], sessionID: string) {
  return sessions.filter((session) => session.id !== sessionID)
}

export function unarchivedSession(session: Session): Session {
  if (session.time.archived === undefined) return session
  return {
    ...session,
    time: {
      ...session.time,
      archived: undefined,
    },
  }
}

export const asActiveSession = unarchivedSession

export function upsertActiveSession(sessions: Session[], session: Session) {
  return [...sessions.filter((item) => item.id !== session.id), asActiveSession(session)].sort((a, b) =>
    a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
  )
}

export function upsertUnarchivedSession(input: { session: Session; setStore: SetStoreFunction<State> }) {
  const session = unarchivedSession(input.session)
  input.setStore(
    produce((draft) => {
      const match = Binary.search(draft.session, session.id, (item) => item.id)
      if (match.found) {
        draft.session[match.index] = session
        return
      }
      draft.session.splice(match.index, 0, session)
      if (!session.parentID) draft.sessionTotal += 1
    }),
  )
  return session
}

export async function unarchiveChat(input: {
  client: Pick<OpenAnalystClient, "session">
  directory: string
  sessionID: string
  sessions: Session[]
  setStore: SetStoreFunction<State>
}) {
  const result = await input.client.session.update({
    directory: input.directory,
    sessionID: input.sessionID,
    time: { archived: null },
  })
  const session = result.data
    ? upsertUnarchivedSession({ session: result.data, setStore: input.setStore })
    : undefined
  return {
    session,
    sessions: removeArchivedChat(input.sessions, input.sessionID),
  }
}

export function archivedChatTimeLabel(session: Session, now: number) {
  return compactSessionTime(session.time.updated ?? session.time.created, now)
}

export function archivedChatDateLabel(time: number | undefined, locale: string) {
  if (time === undefined) return "unknown"
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(time))
}
